import type { Addendum, CosignMetaBody, UUID } from "@/core/types";
import type { ApiRequestWith } from "@/api/types";
import { getGroupAuthType } from "@/utils/authToken";
import { GROUP_AUTH_HEADER_NAME } from "@/api/constants";
import { httpAuth2 } from "@/api/auth";

type NoteAddendumsBody = { patient_id: UUID } & ({ note_id: UUID } | { note_ids: UUID[] });

type CreateNoteAddendumPayload = {
  addendum: string;
  credentials: string | undefined;
  note_id: UUID;
  signature: string | undefined;
  timestamp: number;
};

type UpdateNoteAddendumPayload = CreateNoteAddendumPayload & {
  addendum_id: UUID;
};

type AddendumRequestsTyped = {
  create_note_addendum: ApiRequestWith<
    Addendum,
    { body: CreateNoteAddendumPayload; urlParams: { patientId: UUID } }
  >;
  get_note_addendum_cosign_details: ApiRequestWith<
    CosignMetaBody,
    { urlParams: { addendumId: string } }
  >;
  get_note_addendums: ApiRequestWith<
    { addendums: Addendum[] },
    { body: NoteAddendumsBody; urlParams: { patientId: UUID } }
  >;
  update_note_addendum: ApiRequestWith<
    Addendum,
    { body: UpdateNoteAddendumPayload; urlParams: { patientId: UUID } }
  >;
};

export const addendumRequestsTyped: AddendumRequestsTyped = {
  create_note_addendum: function ({ body, context, urlParams }): Promise<Addendum> {
    return httpAuth2
      .post(`v1/patient_management/${urlParams.patientId}/create_note_addendum`, {
        context,
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
        json: body,
      })
      .json<Addendum>();
  },

  get_note_addendum_cosign_details: function ({ context, urlParams }) {
    return httpAuth2
      .get(`/v1/cosignature/note_addendum/${urlParams.addendumId}`, {
        context: { showLoader: false, ...context },
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
      })
      .json<CosignMetaBody>();
  },

  get_note_addendums: function ({ body, context, urlParams }) {
    return httpAuth2
      .post(`v1/patient_management/${urlParams.patientId}/get_note_addendums`, {
        context,
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
        json: body,
      })
      .json<{ addendums: Addendum[] }>();
  },

  update_note_addendum: function ({ body, context, urlParams }): Promise<Addendum> {
    return httpAuth2
      .post(`v1/patient_management/${urlParams.patientId}/update_note_addendum`, {
        context,
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
        json: body,
      })
      .json<Addendum>();
  },
};
